



























































































import _cloneDeep from 'lodash/cloneDeep'
import { AxiosError } from 'axios'
import { defineComponent, ref, Ref, toRefs, computed, PropType, ComputedRef, onBeforeMount } from '@vue/composition-api'
import useVuelidate from '@vuelidate/core'
import router from '@/router'
import store from '@/store'
import MenuItem from '@/components/MenuItem.vue'
import CommonScrollContainer from '@/components/Common/CommonScrollContainer.vue'
import useDepartments from '@sm/client/components/SchedulingManagement/departments/useDepartments'
import { $hasAccess } from '@/plugins/permissions'
import { Department, DepartmentID } from '@shared/sm/departments/types'

export default defineComponent({
  components: { MenuItem, CommonScrollContainer },
  props: {
    departmentId: {
      type: String as PropType<DepartmentID>,
      required: true,
    },
  },
  setup (props, { root }) {
    const { departmentId } = toRefs(props)

    const department: Ref<Department | undefined> = ref(undefined)
    const departmentChanges: Ref<Department | undefined> = ref(undefined)

    const departmentWithChanges: ComputedRef<Department | object> = computed(() => ({
      ...department.value,
      ...departmentChanges.value,
    }))

    const {
      fetchDepartmentById,
      updateDepartment,
      isUpdatingDepartment,
      isDeletingDepartment,
      removeDepartment,
    } = useDepartments()

    // @ts-expect-error
    // Expected 2-3 arguments, but got 0.
    // TODO: Vuelidate should have optional args notation
    const $v = useVuelidate()

    const isDepartmentInvalid = computed(() => $v.value.$invalid)

    const hasEditDepartmentPermission = computed(() => $hasAccess()
      .to('institutionSettings')
      .to('editDepartmentSettings')
      .value()
    )

    const canSaveDepartment = computed(() =>
      !!department.value &&
      !!departmentChanges.value &&
      hasEditDepartmentPermission.value &&
      !isDepartmentInvalid.value &&
      !isUpdatingDepartment.value
    )

    const canDeleteDepartment = computed(() =>
      !isDeletingDepartment.value &&
      hasEditDepartmentPermission.value
    )

    const tooltipVariant = computed(() => {
      if (!hasEditDepartmentPermission.value) return 'warning'
      if (isDepartmentInvalid.value) return 'danger'
      return 'gray'
    })

    onBeforeMount(async () => {
      try {
        department.value = await fetchDepartmentById(departmentId.value)
      } catch (err) {
        const error = err as AxiosError
        const status = error?.response?.status
        // We don't have any page to display Validation error,
        // so we just redirect to 404 (since Department is invalid).
        // We will report to Bugsnag though + display ApiError.
        if (status === 418 || status === 422) {
          router.push({ name: 'NotFound' })
        }
      }
    })

    function updateChanges (dept: Department) {
      departmentChanges.value = dept
    }

    async function saveDepartment () {
      store.dispatch('setCurrentPut', 'updateDepartment')
      await updateDepartment(departmentWithChanges.value as Department)
      department.value = _cloneDeep(departmentWithChanges.value) as Department
      departmentChanges.value = undefined
      store.dispatch('unsetCurrentPut', 'updateDepartment')
    }

    function confirmDeleteDepartment () {
      root.$confirm({
        message: `Are you sure you want to delete ` +
          `the ${department.value?.displayName || departmentId.value} ` +
          `from the system? This action cannot be undone.`,
        onConfirm: async close => {
          await removeDepartment(departmentId.value)
          close()
          goBack()
        },
        onCancel: close => close(),
      })
    }

    const menuItems = [
      {
        to: 'smDepartmentSettings',
        icon: 'settings',
        label: 'Settings',
      },
      {
        to: 'smDepartmentWorkflowStep',
        icon: 'list_alt',
        label: 'Workflow Step',
      },
    ]

    function goBack () {
      root.$goBack({ name: 'smSettingsDepartments' }, [ 'smSettingsDepartments' ])
    }

    return {
      department,
      departmentChanges,
      departmentWithChanges,
      canSaveDepartment,
      canDeleteDepartment,
      isDepartmentInvalid,
      updateChanges,
      saveDepartment,
      isUpdatingDepartment,
      isDeletingDepartment,
      confirmDeleteDepartment,
      hasEditDepartmentPermission,
      hasDeleteDepartmentPermission: hasEditDepartmentPermission,
      tooltipVariant,
      menuItems,
      goBack,
    }
  },
})
